import { Box, Heading, Select, Text } from 'grommet';
import React, { useMemo, useRef } from 'react';

import HelpTooltip from '@helpers/HelpTooltip';

import { PaywallPageContext } from '@components/Paywall/paywall.types';

import useEntitlements from '@hooks/monetization/useEntitlements';

import Invites from '@features/Settings/Invites';

import { ClaimedPlayer } from '../claimedPlayer';
import { usePaywallContext } from '@contexts/PaywallContext';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import SHDText from '@ui/Text/SHDText';
import SHDImage from '@ui/Image/SHDImage';

const PlayerSettingsTab: React.FC<{
  value: ClaimedPlayer | undefined;
  updateValues: (value: Partial<ClaimedPlayer>) => void;
  error: string | null;
  closeLayer: () => void;
  isBBSB: boolean;
}> = ({ value, updateValues, error }) => {
  // Paywall
  const { entitlementStatus, canEditMonetizedFields } = useEntitlements({
    playerId: value?.id || '',
    sport: value?.primarySport,
  });

  const { handlePaywallOpen } = usePaywallContext({
    entitlementStatus,
    trackingContext: PaywallPageContext.PLAYER_SETTINGS,
  });

  const selectRef = useRef<HTMLInputElement | null>(null);

  const getNameStyleValue = () => {
    if (value?.embedNameFirstLevel === 2 && value?.embedNameLastLevel === 2) {
      return 40;
    }
    if (value?.embedNameFirstLevel === 2 && value?.embedNameLastLevel === 1) {
      return 30;
    }
    if (value?.embedNameFirstLevel === 1 && value?.embedNameLastLevel === 2) {
      return 20;
    }
    if (value?.embedNameFirstLevel === 1 && value?.embedNameLastLevel === 1) {
      return 10;
    }
    return 0;
  };

  const getNameStyleOptions = useMemo(() => {
    return [
      {
        label: `${value?.nameFirst} ${value?.nameLast}`,
        value: 40,
      },
      {
        label: `${value?.nameFirst} ${value?.nameLast ? value?.nameLast.slice(0, 1) : ''}.`,
        value: 30,
      },
      {
        label: `${value?.nameFirst ? value?.nameFirst.slice(0, 1) : ''}. ${value?.nameLast}`,
        value: 20,
      },
      {
        label: `${value?.nameFirst ? value?.nameFirst.slice(0, 1) : ''}.${value?.nameLast ? value?.nameLast.slice(0, 1) : ''}.`,
        value: 10,
      },
      {
        label: "Don't display",
        value: 0,
      },
    ];
  }, [value?.nameFirst, value?.nameLast]);

  return (
    <Box
      fill="horizontal"
      height="100%"
      pad={{ horizontal: 'medium', top: 'medium', bottom: '108px' }}
      gap="medium"
      overflow="auto"
      id="edit-player-settings-box"
    >
      <Text color="status-critical">{error}</Text>
      <Box style={{ flexShrink: 0 }}>
        <Invites
          claimedPlayerId={value?.id}
          nameToManage={value?.nameFirst}
          tooltip={
            <HelpTooltip content="This user would be able to edit player information and settings." />
          }
        />
      </Box>
      <Heading margin={{ bottom: 'small' }} level="5">
        Player settings
      </Heading>
      {error && <Text color="status-critical">{error}</Text>}
      <Box gap="small" style={{ flexShrink: 0 }}>
        <Text>Name display style</Text>
        <Select
          placeholder="Select"
          labelKey="label"
          value={getNameStyleValue()}
          valueKey={{ key: 'value', reduce: true }}
          options={getNameStyleOptions}
          onChange={({ option }) => {
            const embed = {
              embedNameFirstLevel: 0,
              embedNameLastLevel: 0,
              isNameOnPublicWebsite: false,
            };
            if (option.value === 40) {
              embed.embedNameFirstLevel = 2;
              embed.embedNameLastLevel = 2;
            } else if (option.value === 30) {
              embed.embedNameFirstLevel = 2;
              embed.embedNameLastLevel = 1;
            } else if (option.value === 20) {
              embed.embedNameFirstLevel = 1;
              embed.embedNameLastLevel = 2;
            } else if (option.value === 10) {
              embed.embedNameFirstLevel = 1;
              embed.embedNameLastLevel = 1;
            }
            updateValues({ ...embed });
          }}
        />
      </Box>
      {value?.embedNameFirstLevel || value?.embedNameLastLevel ? (
        <Box gap="small" style={{ flexShrink: 0 }}>
          <Text>Show name on public player and team pages</Text>
          <Select
            placeholder="Select"
            labelKey="label"
            value={value?.isNameOnPublicWebsite || false}
            valueKey={{ key: 'value', reduce: true }}
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
            onChange={({ option }) => {
              updateValues({ isNameOnPublicWebsite: option.value });
            }}
          />
        </Box>
      ) : null}
      <Box gap="small" style={{ flexShrink: 0 }}>
        <Text>Show profile picture on public player and team pages</Text>
        <Select
          placeholder="Select"
          labelKey="label"
          value={value?.isEmbedImagePublicWebsite}
          valueKey={{ key: 'value', reduce: true }}
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onChange={({ option }) => {
            updateValues({ isEmbedImagePublicWebsite: option.value });
          }}
        />
      </Box>
      <Box gap="small" style={{ flexShrink: 0 }}>
        <Text>Show profile picture on team livestreams</Text>
        <Select
          placeholder="Select"
          labelKey="label"
          value={value?.isEmbedImageVideo || false}
          valueKey={{ key: 'value', reduce: true }}
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          onChange={({ option }) => {
            updateValues({ isEmbedImageVideo: option.value });
          }}
        />
      </Box>

      <Box gap="small" style={{ flexShrink: 0 }}>
        <SHDThemeOverride full={false}>
          <Box
            pad={{ top: 'xxsmall' }}
            direction="row"
            gap="xsmall"
            align="center"
          >
            <SHDText style="subhead">Show game clips to public</SHDText>
            <SHDImage
              src="/paywall/pro_primary.svg"
              width={49.9}
              alt="edit_player_pro_logo"
            />
          </Box>
        </SHDThemeOverride>

        <Select
          onClick={() => {
            !canEditMonetizedFields && handlePaywallOpen();
          }}
          ref={selectRef}
          placeholder="Select"
          labelKey="label"
          value={value?.publicPublishLevel}
          valueKey={{ key: 'value', reduce: true }}
          options={[
            { label: 'Yes', value: 10 },
            canEditMonetizedFields && { label: 'No', value: 5 },
          ]}
          onChange={({ option }) => {
            canEditMonetizedFields &&
              updateValues({ publicPublishLevel: option.value });
          }}
        />
      </Box>
    </Box>
  );
};

export default PlayerSettingsTab;
